/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Ayi R
*/

import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { View } from 'react-native';
import { isWeb } from '@global/screens';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import SociollaText from '@components/sociolla/SociollaText';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import MoreButton from '@components/navbar/MoreButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import * as Layout from '@components/screen/Layout';
import { hasNoAccess, exportCsvApi } from '@global/helpers';
import { showPopup } from '@/store/modal';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetAccountGroupListQuery,
  useGetAccountGroupCountQuery,
  useGetAccountGroupInfiniteListInfiniteQuery,
} from '@api/listApi';

const tableStructure = {
  code: { head: 'Code', body: 'code' },
  parent_group: { head: 'Parent Group', body: 'parent_group' },
  total_products_premium: {
    head: 'Premium Products',
    body: 'total_products_premium',
  },
  total_products_non_premium: {
    head: 'Non-Premium Products',
    body: 'total_products_non_premium',
  },
  is_active: { head: 'Status', body: 'is_active', type: 'status_active' },
};

// Search-only list: the search box writes `code`; unwrap it into a $regex query.
// (is_account:true is baked into the resource's defaultFilter.)
const toApiFilter = (raw = {}) => {
  const filter = {};
  const code = raw.code?.$regex ?? raw.code;
  if (code) {
    filter.code = { $regex: code, $options: 'i' };
  }
  return filter;
};

// Display row (RTK cache frozen → new object): flatten parent group code, default totals.
const mapRow = (item) => ({
  ...item,
  id: item._id,
  parent_group: item.parent_group?.code,
  total_products_premium: item.total_products_premium || 0,
  total_products_non_premium: item.total_products_non_premium || 0,
});

const AccountGroupScreen = (props) => {
  // ── selectors ──
  const dispatch = useDispatch();

  // ── derived ──
  const hasAccess = hasNoAccess([
    'regional_manager',
    'regional_support',
    'key_account_manager',
    'group_key_account_manager',
    'national_key_account_manager',
    'distributor_manager',
    'general_manager',
    'vice_president',
    'regional_distributor_manager',
  ]);
  const canViewDetail = hasAccess || !hasNoAccess(['distributor_manager']);

  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetAccountGroupListQuery,
    countHook: useGetAccountGroupCountQuery,
    infiniteHook: useGetAccountGroupInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  // ── memoized values ──
  const searchValue = useMemo(() => {
    const k = params.filter?.code;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  const rows = useMemo(() => {
    const raw = isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
    return raw.map(mapRow);
  }, [list.data]);

  // ── effects ──
  // Own the header menu so the mobile Add goes through openChild (preserves the
  // list on return) instead of the navigator's plain navigate.
  useEffect(() => {
    props.navigation.setOptions({
      headerRight: () => {
        if (isWeb) {
          return <NotificationSettingButton {...props} />;
        }
        const menus = [];
        if (hasAccess) {
          menus.push({
            type: 'function',
            name: 'Add',
            callback: () => openChild('AccountGroupFormScreen'),
          });
        }
        return <MoreButton menu={menus} {...props} />;
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.navigation, hasAccess]);

  // ── handlers ──
  const onEdit = ({ data }) => {
    // canViewDetail: edit-access OR distributor_manager (view-only detail) — uat.
    if (canViewDetail) {
      openChild('AccountGroupFormScreen', {
        id: data.id,
        accountID: data.id,
        titleScreen: data.code,
      });
    }
  };

  const handleExport = async () => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      await exportCsvApi(
        'account-group',
        { is_deleted: false, is_account: true },
        {
          isDownload: false,
          message: 'Export in progress, the result will be sent to your email.',
        },
      );
      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  // ── render ──
  return (
    <Layout.Container>
      {isWeb ? (
        <>
          <Layout.Title title="Account Group">
            <View style={{ flexDirection: 'row', gap: 10 }}>
              <Button
                type="outline-primary"
                rounded={true}
                buttonStyle={{ width: 100 }}
                title={
                  <SociollaText
                    type="medium"
                    style={{ fontSize: 14, color: Colors.blue1 }}>
                    Export
                  </SociollaText>
                }
                onPress={handleExport}
              />
              {hasAccess ? (
                <Button
                  type="primary"
                  rounded={true}
                  buttonStyle={{ width: 100 }}
                  title={
                    <SociollaText
                      type="medium"
                      style={{ fontSize: 14, color: Colors.white }}>
                      + Add
                    </SociollaText>
                  }
                  onPress={() => openChild('AccountGroupFormScreen')}
                />
              ) : (
                false
              )}
            </View>
          </Layout.Title>

          <Table
            filterName={scope}
            searchKey="code"
            searchPlaceholder="Search by Account Group Code"
            structures={tableStructure}
            data={rows}
            loading={list.isLoading}
            refetching={list.isFetching}
            hasNoHide={true}
            totalData={count.data}
            skip={params.skip}
            limit={params.limit}
            hasCallback={true}
            showFilter={false}
            onClickRow={(val) => onEdit(val)}
            searchValue={searchValue}
          />
        </>
      ) : (
        <TableMobile
          filterName={scope}
          searchKey="code"
          placeholder="Search by account group code"
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          data={rows}
          totalData={count.data}
          searchValue={searchValue}
          renderItem={({ data, index }) => (
            <Layout.ItemMobile
              key={index}
              touchable={true}
              column={true}
              onPress={() => onEdit({ data: data })}>
              <SociollaText
                type="medium"
                style={{ width: '100%', marginBottom: 4 }}>
                {data.code}
              </SociollaText>
            </Layout.ItemMobile>
          )}
        />
      )}
    </Layout.Container>
  );
};

export default AccountGroupScreen;
